import RefreshIcon from '@mui/icons-material/Refresh'
import { Alert, Box, Button, Tooltip, Typography } from '@mui/material'
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { useGetFailedVendors, useRetryFailedVendor } from '@src/api/failed-vendors-api'
import { FailedVendorSyncItem } from '@src/types/failed-vendor-sync'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface FailedVendorsListProps {
  companyId: string
}

interface MessageState {
  type: 'success' | 'error'
  text: string
}

const FailedVendorsList = ({ companyId }: FailedVendorsListProps) => {
  const { t } = useTranslation()
  const [message, setMessage] = useState<MessageState | null>(null)
  const [retryingMerchantId, setRetryingMerchantId] = useState<string | null>(null)

  const { data: vendors, isLoading, error } = useGetFailedVendors(companyId)
  const retryVendor = useRetryFailedVendor(companyId)

  const handleRetry = useCallback(
    async (merchantId: string) => {
      setRetryingMerchantId(merchantId)
      try {
        const result = await retryVendor.mutateAsync(merchantId)
        if (result.success) {
          setMessage({
            type: 'success',
            text: t('admin.transactionRetry.failedVendors.messages.retrySuccess', { merchantId }),
          })
        } else {
          setMessage({
            type: 'error',
            text: t('admin.transactionRetry.failedVendors.messages.retryFailed', {
              merchantId,
              error: result.errorReason ?? t('admin.transactionRetry.errors.unknown'),
            }),
          })
        }
      } catch {
        setMessage({
          type: 'error',
          text: t('admin.transactionRetry.failedVendors.messages.retryFailed', {
            merchantId,
            error: t('admin.transactionRetry.errors.unknown'),
          }),
        })
      } finally {
        setRetryingMerchantId(null)
      }
    },
    [retryVendor, t],
  )

  const columns: GridColDef<FailedVendorSyncItem>[] = [
    {
      field: 'merchantId',
      headerName: t('admin.transactionRetry.failedVendors.columns.merchantId'),
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'merchantName',
      headerName: t('admin.transactionRetry.failedVendors.columns.name'),
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'errorReason',
      headerName: t('admin.transactionRetry.failedVendors.columns.error'),
      flex: 2,
      minWidth: 250,
      renderCell: (params) => (
        <Tooltip title={params.value as string}>
          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'attemptCount',
      headerName: t('admin.transactionRetry.failedVendors.columns.attempts'),
      width: 100,
      type: 'number',
    },
    {
      field: 'lastFailedAt',
      headerName: t('admin.transactionRetry.failedVendors.columns.lastFailedAt'),
      width: 180,
      valueFormatter: (value: string) => (value ? new Date(value).toLocaleString() : ''),
    },
    {
      field: 'actions',
      headerName: t('admin.transactionRetry.failedVendors.columns.actions'),
      width: 140,
      sortable: false,
      renderCell: (params: GridRenderCellParams<FailedVendorSyncItem>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<RefreshIcon fontSize="small" />}
            disabled={retryingMerchantId === params.row.merchantId}
            onClick={(e) => {
              e.stopPropagation()
              void handleRetry(params.row.merchantId)
            }}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            {t('admin.transactionRetry.failedVendors.actions.retry')}
          </Button>
        </Box>
      ),
    },
  ]

  const rows = (vendors ?? []).map((v) => ({ ...v, id: `${v.financingCompany}-${v.merchantId}` }))

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('admin.transactionRetry.failedVendors.sectionTitle')}
      </Typography>

      {message && (
        <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : t('admin.transactionRetry.errors.unknown')}
        </Alert>
      )}

      {rows.length === 0 && !isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 120,
            bgcolor: 'background.paper',
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('admin.transactionRetry.failedVendors.messages.empty')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'lastFailedAt', sort: 'desc' }] },
            }}
            sx={{
              '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
            }}
          />
        </Box>
      )}
    </Box>
  )
}

export default FailedVendorsList
